import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { TenantProvider } from './contexts/TenantContext';
import { TenantBrandingProvider } from './contexts/TenantBrandingContext';
import { FeatureFlagsProvider } from './contexts/FeatureFlagsContext';
import { ProtectedRoute, AdminRoute } from './components/auth/ProtectedRoute';
import { DISABLED_FEATURES } from './config/api';
import './App.css';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
// Removed: RegisterPage, ForgotPasswordPage - simplified login flow
import AuthCallback from './pages/AuthCallback';
import DashboardPage from './pages/DashboardPage';
import CourseCatalogPage from './pages/CourseCatalogPage';
import CourseDetailPage from './pages/CourseDetailPage';
import CoursePlayer from './pages/CoursePlayer';
import SCORMPlayer from './pages/SCORMPlayer';
import QuizPlayer from './pages/QuizPlayer';
import WebinarsPage from './pages/WebinarsPage';
import EBooksPage from './pages/EBooksPage';
import EBookDetailPage from './pages/EBookDetailPage';
import CertificatesPage from './pages/CertificatesPage';
import CampaignsPage from './pages/CampaignsPage';
import { LeaderboardPage } from './components/Leaderboard';
import AdminDashboard from './pages/AdminDashboard';
import AICourseGenerator from './pages/AICourseGenerator';
import AICoursePlayer from './pages/AICoursePlayer';
import CourseBuilder from './pages/CourseBuilder';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentCancelPage from './pages/PaymentCancelPage';
import InstructorAssessments from './pages/InstructorAssessments';
import PricingPage from './pages/PricingPage';
import SubscriptionSuccessPage from './pages/SubscriptionSuccessPage';
import LearningPathwaysPage from './pages/LearningPathwaysPage';
import PathwayProgressPage from './pages/PathwayProgressPage';
import MasterclassDetailPage from './pages/MasterclassDetailPage';
import WishlistPage from './pages/WishlistPage';
import WSPPage from './pages/WSPPage';
import SettingsPage from './pages/SettingsPage';
import AboutPage from './pages/AboutPage';
import FAQPage from './pages/FAQPage';

// Learning Lab Pages
import LearningLabCatalog from './pages/LearningLabCatalog';
import LearningLabPlayer from './pages/LearningLabPlayer';
import LearningLabBuilder from './pages/admin/LearningLabBuilder';
import EnterpriseTenantAdmin from './pages/admin/EnterpriseTenantAdmin';
import ExternalLearningPage from './pages/ExternalLearningPage';
import ExternalLearningPlayer from './pages/ExternalLearningPlayer';
import ExternalCoursePlayer from './pages/ExternalCoursePlayer';
import LTILaunch from './pages/LTILaunch';
import AcademyPage from './pages/AcademyPage';
import RegulatoryAlertsPage from './pages/RegulatoryAlertsPage';
import EnterpriseSSOPage from './pages/EnterpriseSSOPage';
import SSOCallbackPage from './pages/SSOCallbackPage';
import CompetencyFrameworkGenerator from './pages/CompetencyFrameworkGenerator';
// Professional Programmes Pages
import ProgrammesPage from './pages/ProgrammesPage';
import ProgrammeDetailPage from './pages/ProgrammeDetailPage';
import ProgrammeBuilderPage from './pages/admin/ProgrammeBuilderPage';
import ProgrammeWorkflowPage from './pages/admin/ProgrammeWorkflowPage';
import ProgrammeLearnersPage from './pages/admin/ProgrammeLearnersPage';
import ProgrammeGradebookPage from './pages/admin/ProgrammeGradebookPage';
// Removed: PlatformAdminLogin - using admin access token instead

// Wrapper to handle OAuth callback
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/enterprise-sso" element={<EnterpriseSSOPage />} />
      <Route path="/sso/callback" element={<SSOCallbackPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Navigate Learning Academy - Public ICA Course Catalog */}
      {/* TEMPORARILY DISABLED - see DISABLED_FEATURES in config/api.js */}
      <Route path="/academy" element={
        DISABLED_FEATURES.navigate_academy ? <Navigate to="/courses" replace /> : <AcademyPage />
      } />
      
      {/* Navigate Learning - Regulatory Alerts */}
      {/* TEMPORARILY DISABLED - see DISABLED_FEATURES in config/api.js */}
      <Route path="/regulatory-alerts" element={
        DISABLED_FEATURES.regulatory_alerts ? <Navigate to="/dashboard" replace /> : <RegulatoryAlertsPage />
      } />
      
      {/* Course Routes (Public viewing, protected enrollment) */}
      <Route path="/courses" element={<CourseCatalogPage />} />
      <Route path="/catalog" element={<Navigate to="/courses" replace />} />
      <Route path="/learning-labs" element={<Navigate to="/labs" replace />} />
      <Route path="/courses/:courseId" element={<CourseDetailPage />} />
      <Route path="/courses/:courseId/learn" element={
        <ProtectedRoute>
          <CoursePlayer />
        </ProtectedRoute>
      } />
      <Route path="/scorm/:packageId" element={
        <ProtectedRoute>
          <SCORMPlayer />
        </ProtectedRoute>
      } />
      <Route path="/quiz/:quizId" element={
        <ProtectedRoute>
          <QuizPlayer />
        </ProtectedRoute>
      } />
      
      {/* Masterclass & eBook Routes (Public) */}
      <Route path="/masterclasses" element={<WebinarsPage />} />
      <Route path="/masterclasses/:webinarId" element={<MasterclassDetailPage />} />
      <Route path="/ebooks" element={<EBooksPage />} />
      <Route path="/ebooks/:ebookId" element={<EBookDetailPage />} />
      <Route path="/learning-pathways" element={<LearningPathwaysPage />} />
      <Route path="/pathways/:pathwayId" element={
        <ProtectedRoute>
          <PathwayProgressPage />
        </ProtectedRoute>
      } />
      <Route path="/pathways" element={<LearningPathwaysPage />} />
      
      {/* WSP Route (Protected) */}
      <Route path="/wsp" element={
        <ProtectedRoute>
          <WSPPage />
        </ProtectedRoute>
      } />
      
      {/* Settings Route (Protected) */}
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      
      {/* Competency Framework Generator (Replaces Global Role Readiness) */}
      <Route path="/competency-framework" element={
        <ProtectedRoute>
          <CompetencyFrameworkGenerator />
        </ProtectedRoute>
      } />
      
      {/* Professional Programmes Routes */}
      <Route path="/programmes" element={<ProgrammesPage />} />
      <Route path="/programmes/:programmeId" element={<ProgrammeDetailPage />} />
      
      {/* Admin Programme Management Routes */}
      <Route path="/admin/programmes/create" element={
        <AdminRoute>
          <ProgrammeWorkflowPage />
        </AdminRoute>
      } />
      <Route path="/admin/programmes/:programmeId/workflow" element={
        <AdminRoute>
          <ProgrammeWorkflowPage />
        </AdminRoute>
      } />
      <Route path="/admin/programmes/:programmeId/edit" element={
        <AdminRoute>
          <ProgrammeBuilderPage />
        </AdminRoute>
      } />
      <Route path="/admin/programmes/:programmeId/builder" element={
        <AdminRoute>
          <ProgrammeBuilderPage />
        </AdminRoute>
      } />
      <Route path="/admin/programmes/:programmeId/learners" element={
        <AdminRoute>
          <ProgrammeLearnersPage />
        </AdminRoute>
      } />
      <Route path="/admin/programmes/:programmeId/gradebook" element={
        <AdminRoute>
          <ProgrammeGradebookPage />
        </AdminRoute>
      } />
      
      {/* Learning Lab Routes */}
      <Route path="/labs" element={<LearningLabCatalog />} />
      <Route path="/labs/:labId" element={
        <ProtectedRoute>
          <LearningLabPlayer />
        </ProtectedRoute>
      } />
      
      {/* External Learning Routes (SCORM Cloud / Third-party content) */}
      <Route path="/external-learning" element={<ExternalLearningPage />} />
      <Route path="/external-learning/:moduleId" element={
        <ProtectedRoute>
          <ExternalLearningPlayer />
        </ProtectedRoute>
      } />
      
      {/* External Courses (Old Mutual / Iframe-based) */}
      <Route path="/external-courses/:courseId" element={
        <ProtectedRoute>
          <ExternalCoursePlayer />
        </ProtectedRoute>
      } />
      
      {/* LTI Launch Route (from Moodle) */}
      <Route path="/lti/launch" element={<LTILaunch />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/my-courses" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/wishlist" element={
        <ProtectedRoute>
          <WishlistPage />
        </ProtectedRoute>
      } />
      {/* Certificates removed per requirements */}
      <Route path="/campaigns" element={
        <ProtectedRoute>
          <CampaignsPage />
        </ProtectedRoute>
      } />
      <Route path="/leaderboard" element={
        <ProtectedRoute>
          <LeaderboardPage />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/cpd" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <AdminRoute>
          <AdminDashboard />
        </AdminRoute>
      } />
      <Route path="/admin/*" element={
        <AdminRoute>
          <AdminDashboard />
        </AdminRoute>
      } />
      
      {/* AI Course Generator Routes */}
      <Route path="/ai-course/create" element={
        <AdminRoute>
          <AICourseGenerator />
        </AdminRoute>
      } />
      <Route path="/courses/:courseId/ai-learn" element={
        <ProtectedRoute>
          <AICoursePlayer />
        </ProtectedRoute>
      } />
      
      {/* Course Builder Routes */}
      <Route path="/admin/course-builder" element={
        <AdminRoute>
          <CourseBuilder />
        </AdminRoute>
      } />
      <Route path="/admin/course-builder/:courseId" element={
        <AdminRoute>
          <CourseBuilder />
        </AdminRoute>
      } />
      
      {/* Learning Lab Builder Routes */}
      <Route path="/admin/labs" element={
        <AdminRoute>
          <LearningLabBuilder />
        </AdminRoute>
      } />
      <Route path="/admin/labs/:labId" element={
        <AdminRoute>
          <LearningLabBuilder />
        </AdminRoute>
      } />
      
      {/* NAVI Instructor Assessments */}
      <Route path="/admin/navi-assessments" element={
        <AdminRoute>
          <InstructorAssessments />
        </AdminRoute>
      } />
      
      {/* Enterprise Tenant Management */}
      <Route path="/admin/enterprise-tenants" element={
        <AdminRoute>
          <EnterpriseTenantAdmin />
        </AdminRoute>
      } />
      
      {/* Payment Routes */}
      <Route path="/payment/success" element={
        <ProtectedRoute>
          <PaymentSuccessPage />
        </ProtectedRoute>
      } />
      <Route path="/payment/cancel" element={<PaymentCancelPage />} />
      <Route path="/payment/paypal/return" element={
        <ProtectedRoute>
          <PayPalReturnPage />
        </ProtectedRoute>
      } />
      
      {/* Subscription Routes */}
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/subscription/success" element={
        <ProtectedRoute>
          <SubscriptionSuccessPage />
        </ProtectedRoute>
      } />
      <Route path="/subscription/cancel" element={<PricingPage />} />
      
      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

// PayPal Return Page (handles capture)
function PayPalReturnPage() {
  const [searchParams] = React.useState(() => new URLSearchParams(window.location.search));
  const paymentId = searchParams.get('payment_id');
  const token = searchParams.get('token'); // PayPal order token
  
  React.useEffect(() => {
    // Redirect to success page - capture happens on backend
    window.location.href = `/payment/success?payment_id=${paymentId}`;
  }, [paymentId]);
  
  return (
    <div className="min-h-screen bg-navigate-dark flex items-center justify-center px-4">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navigate-gold mx-auto mb-4"></div>
        <p className="text-zinc-400">Processing your payment...</p>
      </div>
    </div>
  );
}

// 404 Page
function NotFoundPage() {
  return (
    <div className="min-h-screen bg-navigate-dark flex items-center justify-center px-4">
      <div className="text-center">
        <h1 className="font-heading text-6xl font-bold text-navigate-gold mb-4">404</h1>
        <p className="text-xl text-white mb-2">Page Not Found</p>
        <p className="text-zinc-400 mb-8">
          The page you are looking for does not exist.
        </p>
        <a href="/" className="btn-gold inline-block">
          Go Home
        </a>
      </div>
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <TenantProvider>
          <AuthProvider>
            <TenantBrandingProvider>
              <FeatureFlagsProvider>
                <AppRouter />
                <Toaster 
                  position="top-right"
                  toastOptions={{
                    style: {
                      background: '#18181b',
                      border: '1px solid #27272a',
                      color: '#fafafa',
                    },
                  }}
                />
              </FeatureFlagsProvider>
            </TenantBrandingProvider>
          </AuthProvider>
        </TenantProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
